import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter } from 'lucide-react';
import { productAPI } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import SEO from '../components/SEO';

// Helper to get localized text from product fields
const getLocalizedText = (field, language) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field[language] || field['en'] || field['no'] || '';
};

const ProductsPage = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        category: '',
        origin: '',
    });
    const location = useLocation();
    
    // Scroll to top when page loads
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const { t, language } = useLanguage();

    const origins = ['Slovenia', 'Spain', 'Germany', 'Italy'];
    const categories = [
        { value: 'still', label: t('stillWater') },
        { value: 'sparkling', label: t('sparklingWater') },
    ];

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setLoading(true);
                const params = {};
                if (filters.category && filters.category !== 'all') params.category = filters.category;
                if (filters.origin && filters.origin !== 'all') params.origin = filters.origin;
                
                const res = await productAPI.getAll(params);
                setProducts(res.data);
            } catch (error) {
                console.error('Failed to fetch products:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, [filters]);

    const clearFilters = () => {
        setFilters({ category: '', origin: '' });
    };

    const hasActiveFilters = filters.category || filters.origin;

    const FilterContent = () => (
        <div className="space-y-8">
            <div>
                <label className="text-xs uppercase tracking-[0.15em] text-stone-500 mb-3 block">
                    {t('type')}
                </label>
                <Select 
                    value={filters.category} 
                    onValueChange={(value) => setFilters(prev => ({ ...prev, category: value }))}
                >
                    <SelectTrigger className="rounded-none border-stone-200" data-testid="category-filter">
                        <SelectValue placeholder={t('allTypes')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('allTypes')}</SelectItem>
                        {categories.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div>
                <label className="text-xs uppercase tracking-[0.15em] text-stone-500 mb-3 block">
                    {t('origin')}
                </label>
                <Select 
                    value={filters.origin} 
                    onValueChange={(value) => setFilters(prev => ({ ...prev, origin: value }))}
                >
                    <SelectTrigger className="rounded-none border-stone-200" data-testid="origin-filter">
                        <SelectValue placeholder={t('allOrigins')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('allOrigins')}</SelectItem>
                        {origins.map(origin => (
                            <SelectItem key={origin} value={origin}>{origin}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {hasActiveFilters && (
                <Button 
                    variant="ghost" 
                    onClick={clearFilters}
                    className="w-full text-xs uppercase tracking-wider"
                    data-testid="clear-filters"
                >
                    {t('clearFilters')}
                </Button>
            )}
        </div>
    );

    return (
        <div className="page-transition" data-testid="products-page">
            <SEO 
                title="Produkter"
                description="Utforsk vårt utvalg av premium mineralvann fra Europa. ROI fra Slovenia, Vichy Catalan fra Spania, Gerolsteiner fra Tyskland og Marzia fra Italia."
                canonical="/products"
            />
            {/* Hero Banner */}
            <section className="py-20 md:py-32 bg-stone-50">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <p className="caption mb-4">{t('ourCollection')}</p>
                        <h1 className="font-display text-5xl md:text-7xl font-light">
                            {t('mineralWaters')}
                        </h1>
                        <p className="text-stone-500 mt-6 max-w-xl">
                            {t('productsDescription')}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Products Grid */}
            <section className="py-16 md:py-24">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex flex-col md:flex-row gap-12 md:gap-16">
                        {/* Desktop Filters */}
                        <aside className="hidden md:block w-64 flex-shrink-0">
                            <div className="sticky top-32">
                                <h3 className="font-display text-lg mb-8">{t('filters')}</h3>
                                <FilterContent />
                            </div>
                        </aside>

                        {/* Mobile Filter Button */}
                        <div className="md:hidden flex justify-between items-center mb-4">
                            <p className="text-sm text-stone-500">
                                {products.length} {products.length !== 1 ? t('products') : t('product')}
                            </p>
                            <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                                <SheetTrigger asChild>
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        className="rounded-none"
                                        data-testid="mobile-filter-trigger"
                                    >
                                        <Filter className="w-4 h-4 mr-2" />
                                        {t('filters')}
                                        {hasActiveFilters && (
                                            <span className="ml-2 w-5 h-5 bg-fjord text-white text-xs flex items-center justify-center">
                                                {(filters.category ? 1 : 0) + (filters.origin ? 1 : 0)}
                                            </span>
                                        )}
                                    </Button>
                                </SheetTrigger>
                                <SheetContent side="left" className="w-[300px]">
                                    <SheetHeader>
                                        <SheetTitle>{t('filters')}</SheetTitle>
                                    </SheetHeader>
                                    <div className="mt-8">
                                        <FilterContent />
                                    </div>
                                </SheetContent>
                            </Sheet>
                        </div>

                        {/* Products */}
                        <div className="flex-1">
                            <div className="hidden md:flex justify-between items-center mb-12">
                                <p className="text-sm text-stone-500">
                                    {t('showing')} {products.length} {products.length !== 1 ? t('products') : t('product')}
                                </p>
                            </div>

                            {loading ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                                    {[...Array(4)].map((_, i) => (
                                        <div key={i} className="animate-pulse">
                                            <div className="aspect-[3/4] bg-stone-200 mb-6" />
                                            <div className="h-4 bg-stone-200 w-2/3 mb-2" />
                                            <div className="h-4 bg-stone-200 w-1/3" />
                                        </div>
                                    ))}
                                </div>
                            ) : products.length === 0 ? (
                                <div className="text-center py-20">
                                    <p className="text-stone-500 mb-4">{t('noProductsFound')}</p>
                                    <Button 
                                        variant="outline" 
                                        onClick={clearFilters}
                                        className="rounded-none"
                                    >
                                        {t('clearFilters')}
                                    </Button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                                    {products.map((product, index) => (
                                        <motion.div
                                            key={product.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.5, delay: index * 0.05 }}
                                        >
                                            <Link 
                                                to={`/products/${product.id}`}
                                                className="group block"
                                                data-testid={`product-card-${index}`}
                                            >
                                                <div className="aspect-[3/4] overflow-hidden mb-6 bg-stone-100 flex items-center justify-center">
                                                    <img 
                                                        src={product.image} 
                                                        alt={product.name}
                                                        className="w-full h-full object-contain p-2 transition-transform duration-700 group-hover:scale-105"
                                                    />
                                                </div>
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <h3 className="font-display text-lg mb-1 group-hover:text-fjord transition-colors">
                                                            {product.name}
                                                        </h3>
                                                        <p className="text-xs uppercase tracking-wider text-stone-500">
                                                            {getLocalizedText(product.origin, language)} · {product.bottle_size}
                                                        </p>
                                                    </div>
                                                </div>
                                            </Link>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ProductsPage;
